import { useState } from "react";
import type { Conversation, ConversationStatus } from "@zapir/shared";
import { BottomSheet, SheetAction } from "./BottomSheet";
import { Icon } from "./Icon";
import { Avatar } from "./Avatar";
import { Tag } from "./Tag";
import { Button } from "./Button";
import { Input } from "./Input";
import {
  useAssignConversation,
  usePinConversation,
  useRenameContact,
  useSetConversationLabels,
  useSetConversationStatus,
  useSetRead,
} from "@/hooks/useConversations";
import { useLabels, useMembers } from "@/hooks/useApp";
import { useSession, toast } from "@/lib/store";
import { ZapirApiError } from "@/lib/errors";
import { plural } from "@/lib/format";
import { cx } from "@/lib/cx";

interface Props {
  conversation: Conversation;
  open: boolean;
  onClose: () => void;
  onAddNote?: () => void;
  onMention?: () => void;
}

type Mode = "menu" | "assign" | "labels" | "status" | "rename";

/** Titulo do sheet por passo; substitui a escada de ternarios no JSX. */
const TITLES: Record<Mode, string> = {
  menu: "Ações da conversa",
  assign: "Atribuir conversa",
  labels: "Etiquetar conversa",
  status: "Mudar status",
  rename: "Renomear contato",
};

const STATUSES: { value: ConversationStatus; label: string }[] = [
  { value: "open", label: "Aberta" },
  { value: "pending", label: "Pendente" },
  { value: "snoozed", label: "Adiada" },
  { value: "closed", label: "Fechada" },
];

/**
 * Bottom sheet de acoes da conversa (RF-UX-025..028/034/038/123). Cada acao
 * abre a folha correspondente; a mutacao e otimista com retry em erro.
 */
export function ConversationActionsSheet({ conversation, open, onClose, onAddNote, onMention }: Props) {
  const [mode, setMode] = useState<Mode>("menu");
  const labels = useLabels();
  const members = useMembers();
  const me = useSession((s) => s.me);
  const pin = usePinConversation();
  const assign = useAssignConversation();
  const renameContact = useRenameContact();
  const setLabels = useSetConversationLabels();
  const setStatus = useSetConversationStatus();
  const setRead = useSetRead();

  const [draftLabels, setDraftLabels] = useState<string[]>(conversation.labels.map((l) => l.id));
  const [draftName, setDraftName] = useState(conversation.contactName);

  const close = () => {
    setMode("menu");
    onClose();
  };

  const onAssign = (userId: string | null) => {
    assign.mutate(
      { id: conversation.id, userId, expectedAssigneeId: conversation.assignee?.id ?? null },
      { onSuccess: close },
    );
  };

  return (
    <BottomSheet
      open={open}
      onClose={close}
      title={TITLES[mode]}
      label="Ações da conversa"
    >
      {mode === "menu" ? (
        <>
          <SheetAction
            icon={<PinIndicatorIcon />}
            onClick={() => pin.mutate({ id: conversation.id, pinned: !conversation.pinnedAt }, { onSuccess: close })}
            trailing={<span className="text-[11px] muted">{conversation.pinnedAt ? "fixada na sua lista" : "só na minha lista"}</span>}
          >
            <span className={cx(conversation.pinnedAt && "font-semibold text-[color:var(--tg-primary-text)]")}>
              {conversation.pinnedAt ? "Desafixar conversa" : "Fixar conversa"}
            </span>
          </SheetAction>
          <SheetAction
            icon={<Icon name="user" size={16} />}
            onClick={() => setMode("assign")}
            trailing={<span className="text-[11px] muted">{conversation.assignee ? `@${conversation.assignee.name.split(" ")[0]}` : "sem responsável"}</span>}
          >
            Atribuir
          </SheetAction>
          <SheetAction
            icon={<Icon name="note" size={16} />}
            onClick={() => {
              setDraftName(conversation.contactName);
              setMode("rename");
            }}
          >
            Renomear contato
          </SheetAction>
          <SheetAction
            icon={<Icon name="label" size={16} />}
            onClick={() => setMode("labels")}
            trailing={conversation.labels.slice(0, 2).map((l) => <Tag key={l.id} label={l.name} color={l.color} />)}
          >
            Etiquetar
          </SheetAction>
          <SheetAction
            icon={<Icon name="eye" size={16} />}
            onClick={() => {
              setRead.mutate({ id: conversation.id, read: false });
              toast({ tone: "info", message: "Conversa marcada como não lida." });
              close();
            }}
          >
            Marcar como não lido
          </SheetAction>
          <SheetAction
            icon={<Icon name="note" size={16} />}
            onClick={() => {
              onAddNote?.();
              close();
            }}
          >
            Adicionar nota interna
          </SheetAction>
          <SheetAction
            icon={<span className="font-bold">@</span>}
            onClick={() => {
              onMention?.();
              close();
            }}
          >
            Mencionar alguém
          </SheetAction>
          <SheetAction
            icon={<Icon name="check" size={16} />}
            onClick={() => setMode("status")}
            trailing={<span className="text-[11px] muted">{STATUSES.find((s) => s.value === conversation.status)?.label}</span>}
          >
            Mudar status
          </SheetAction>
          <SheetAction
            icon={<Icon name="mute" size={16} />}
            onClick={() => {
              toast({ tone: "info", message: "Silenciar chega numa próxima versão." });
              close();
            }}
          >
            Silenciar por 8 horas
          </SheetAction>
          <SheetAction
            icon={<Icon name="export" size={16} />}
            onClick={() => {
              toast({ tone: "info", message: "Exportar conversa chega numa próxima versão." });
              close();
            }}
          >
            Exportar conversa
          </SheetAction>
        </>
      ) : null}

      {mode === "assign" ? (
        <>
          {(members.data ?? []).map((member) => (
            <SheetAction
              key={member.id}
              icon={<RadioCheck on={conversation.assignee?.id === member.id} />}
              onClick={() => onAssign(member.id)}
              trailing={
                conversation.assignee?.id === member.id ? <span className="text-[11px] muted">atual</span> : null
              }
            >
              <span className="flex items-center gap-3">
                <Avatar name={member.name} size={32} />
                <span className="flex flex-col">
                  <span className="font-semibold">
                    {member.name}
                    {member.id === me?.id ? " (você)" : ""}
                  </span>
                  <span className="text-[11px] muted">
                    {member.role}
                    {member.channels !== undefined ? ` · ${plural(member.channels, "canal", "canais")}` : ""}
                  </span>
                </span>
              </span>
            </SheetAction>
          ))}
          <SheetAction icon={<span aria-hidden="true" className="radio-dot" />} onClick={() => onAssign(null)}>
            Remover responsável
          </SheetAction>
          <BackToMenu onClick={() => setMode("menu")} />
        </>
      ) : null}

      {mode === "labels" ? (
        <>
          {(labels.data ?? []).map((label) => {
            const checked = draftLabels.includes(label.id);
            return (
              <SheetAction
                key={label.id}
                icon={<Icon name="check" size={16} className={checked ? "text-brand" : "opacity-25"} />}
                current={checked}
                onClick={() =>
                  setDraftLabels((current) =>
                    current.includes(label.id) ? current.filter((id) => id !== label.id) : [...current, label.id],
                  )
                }
                trailing={<Tag label={label.name} color={label.color} />}
              >
                {label.description ?? label.name}
              </SheetAction>
            );
          })}
          {(labels.data ?? []).length === 0 ? (
            <p className="py-2 text-sm muted">Nenhuma etiqueta criada ainda.</p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button className="flex-1" onClick={() => setMode("menu")}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                setLabels.mutate(
                  { id: conversation.id, labelIds: draftLabels },
                  {
                    onSuccess: close,
                    onError: (error) => {
                      if (error instanceof ZapirApiError && error.code === "LABEL_NAME_CONFLICT") {
                        toast({ tone: "danger", message: error.copy, actionLabel: error.action });
                      }
                    },
                  },
                );
              }}
            >
              Aplicar
            </Button>
          </div>
        </>
      ) : null}

      {mode === "status" ? (
        <>
          {STATUSES.map((status) => (
            <SheetAction
              key={status.value}
              icon={<RadioCheck on={conversation.status === status.value} />}
              onClick={() => setStatus.mutate({ id: conversation.id, status: status.value }, { onSuccess: close })}
            >
              {status.label}
            </SheetAction>
          ))}
          <BackToMenu onClick={() => setMode("menu")} />
        </>
      ) : null}

      {mode === "rename" ? (
        <>
          <p className="mb-3 text-[13px] muted">
            Esse nome fica salvo pra sua equipe e passa a valer no lugar do nome que a pessoa usa no WhatsApp.
          </p>
          <Input label="Nome do contato" value={draftName} onChange={(event) => setDraftName(event.target.value)} />
          <div className="mt-4 flex gap-2">
            <Button className="flex-1" onClick={() => setMode("menu")}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              disabled={!draftName.trim() || renameContact.isPending}
              onClick={() =>
                renameContact.mutate({ id: conversation.id, name: draftName.trim() }, { onSuccess: close })
              }
            >
              {renameContact.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </>
      ) : null}
    </BottomSheet>
  );
}

function PinIndicatorIcon() {
  return <Icon name="pin" size={17} style={{ color: "var(--brand)" }} />;
}

/** Marca da opcao escolhida nas listas de radio (atribuir / status). */
function RadioCheck({ on }: { on: boolean }) {
  return on ? (
    <Icon name="check" size={16} className="text-brand" />
  ) : (
    <span aria-hidden="true" className="radio-dot" />
  );
}

function BackToMenu({ onClick }: { onClick: () => void }) {
  return (
    <div className="mt-3">
      <Button block onClick={onClick}>
        Voltar
      </Button>
    </div>
  );
}
